import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Activity, FlaskConical, Stethoscope, Pill,
  ChevronRight, MapPin, Clock, Quote, ArrowRight,
  ChevronLeft, ChevronDown, Calendar, Users, Award, BookOpen, Star,
  X, ZoomIn, Shield, GraduationCap,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { getImageUrl, fetchGaleriBeranda, fetchBeritaList, formatTanggalIndonesia, fetchAgenda, fetchSlider, fetchTestimoni, fetchStudyPrograms } from '../utils/api';
import useEmblaCarousel from 'embla-carousel-react';
import HomePopupModal from '../components/HomePopupModal';

const CAMPUS = {
  students: 'https://storage.stikes-hi.ac.id/stikes-hi-media/6c8665c5-9492-456b-812b-77340c267144.webp',
  garden: 'https://storage.stikes-hi.ac.id/stikes-hi-media/e4787803-6681-4c02-847d-2e1a5acdfd89.webp',
  lab: 'https://storage.stikes-hi.ac.id/stikes-hi-media/200cdb75-e757-4c35-957f-e8cdea466525.webp',
  lecture: 'https://storage.stikes-hi.ac.id/stikes-hi-media/39785e01-06e7-4548-be0b-78557d1e5079.webp',
};

// Count-up hook
const useCountUp = (target, duration = 2000, started = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started) return;
    let current = 0;
    const steps = duration / 50;
    const increment = target / steps;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else { setCount(Math.floor(current)); }
    }, 50);
    return () => clearInterval(timer);
  }, [started, target, duration]);
  return count;
};

// IntersectionObserver hook
const useInView = (threshold = 0.2) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
};

// Lazy-render sections below the fold to reduce initial TBT
const LazySection = ({ children, minHeight = '400px' }) => {
  const ref = useRef(null);
  const [rendered, setRendered] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRendered(true); observer.disconnect(); } },
      { rootMargin: '200px 0px', threshold: 0 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref}>
      {rendered ? children : <div style={{ minHeight }} />}
    </div>
  );
};

// ===================== HERO SECTION =====================
const HeroSection = () => {
  const { isDark } = useTheme();
  const particles = useMemo(() =>
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      size: Math.random() * 5 + 2,
      left: Math.random() * 100,
      top: Math.random() * 100,
      duration: Math.random() * 12 + 8,
      delay: Math.random() * 6,
      opacity: Math.random() * 0.3 + 0.1,
      variant: i % 3 + 1,
    })), []
  );

  const heroGradient = isDark
    ? 'linear-gradient(160deg, #2D1060 0%, #7B2FBE 25%, #1E1B4B 60%, #0A0A0F 100%)'
    : 'linear-gradient(160deg, #4C1D95 0%, #7B2FBE 30%, #6D28D9 60%, #5B21B6 100%)';

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: heroGradient }}
    >
      {particles.map(p => (
        <div
          key={p.id}
          className={`particle-${p.variant} absolute rounded-full bg-white pointer-events-none`}
          style={{
            width: p.size, height: p.size,
            left: `${p.left}%`, top: `${p.top}%`,
            opacity: p.opacity,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-purple-600/20 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-indigo-600/15 blur-3xl pointer-events-none" />
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-4 py-2 text-white text-sm font-medium mb-6">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            Kampus Kesehatan Terbaik di Jambi
          </span>
        </motion.div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-3 font-heading">
          Sekolah Tinggi Ilmu Kesehatan
        </h1>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 font-heading text-[#fc76d8] dark:text-[#f94b99]">
          Harapan Ibu Jambi
        </h1>
        <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
          className="text-white/80 text-lg sm:text-xl max-w-2xl mx-auto leading-relaxed mb-10">
          Unggul, Berkarakter, Berdaya Saing dalam Ilmu Kesehatan untuk Indonesia
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.45 }}
          className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="https://pmb.stikes-hi.ac.id/" target="_blank" rel="noopener noreferrer"
            className="bg-white text-[#7B2FBE] px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-purple-50 transition-all duration-300 hover:scale-105 active:scale-95 shadow-lg shadow-black/20">
            Daftar Sekarang
          </a>
          <Link to="/sejarah"
            className="border-2 border-white/60 text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-white/10 transition-all duration-300 hover:scale-105 active:scale-95 backdrop-blur-sm">
            Jelajahi Kampus
          </Link>
        </motion.div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-white/60 text-xs">Scroll</span>
        <ChevronDown size={20} className="text-white/60 animate-bounce" />
      </div>
    </section>
  );
};

// ===================== BANNER SECTION =====================
const BannerSkeleton = () => (
  <div className="flex-[0_0_100%] min-w-0 aspect-[16/7] md:aspect-[19/7] rounded-2xl sm:rounded-3xl bg-gray-200 dark:bg-gray-800 animate-pulse" />
);

const BannerSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch dari CMS
  useEffect(() => {
    fetchSlider().then((data) => {
      setBanners(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Carousel Logic
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);

    // Auto slide simple implementation
    const interval = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 5000);
    return () => {
      clearInterval(interval);
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, banners.length]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();
  const scrollTo = (index) => emblaApi && emblaApi.scrollTo(index);

  if (!loading && banners.length === 0) return null;

  return (
    <section data-testid="banner-section" className="pt-10 pb-8 bg-white dark:bg-[#121020]">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-6">
        <div className="relative group">
          <div className="overflow-hidden rounded-2xl sm:rounded-3xl" ref={emblaRef}>
            <div className="flex">
              {loading ? <BannerSkeleton /> : banners.map((item) => (
                <div key={item.id} className="relative flex-[0_0_100%] min-w-0 aspect-[16/7] md:aspect-[19/7] rounded-2xl sm:rounded-3xl overflow-hidden cursor-pointer shadow-sm border border-gray-100 dark:border-gray-800">
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors z-10 pointer-events-none" />
                  {item.link && item.link.startsWith('http') ? (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="block w-full h-full">
                      <img src={getImageUrl(item.gambar, { width: 1200, quality: 85, format: 'webp' })} alt={item.judul} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                    </a>
                  ) : item.link ? (
                    <Link to={item.link} className="block w-full h-full">
                      <img src={getImageUrl(item.gambar, { width: 1200, quality: 85, format: 'webp' })} alt={item.judul} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                    </Link>
                  ) : (
                    <div className="block w-full h-full">
                      <img src={getImageUrl(item.gambar, { width: 1200, quality: 85, format: 'webp' })} alt={item.judul} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
                    </div>
                  )}
                  {/* Text Overlay (jika tidak ada gambar text-embedded) */}
                  {(item.judul || item.deskripsi) && (
                    <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 z-20 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {item.judul && <h3 className="text-xl md:text-3xl font-bold text-white drop-shadow-md font-heading max-w-xl">{item.judul}</h3>}
                      {item.deskripsi && <p className="text-sm md:text-base text-gray-200 drop-shadow max-w-lg mt-1">{item.deskripsi}</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {!loading && banners.length > 1 && (
            <>
              <button onClick={scrollPrev} className="absolute left-3 md:-left-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 dark:bg-black/60 text-[#1A1A2E] dark:text-white shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-white hover:scale-110 z-30">
                <ChevronLeft size={20} />
              </button>
              <button onClick={scrollNext} className="absolute right-3 md:-right-5 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 dark:bg-black/60 text-[#1A1A2E] dark:text-white shadow-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all hover:bg-white hover:scale-110 z-30">
                <ChevronRight size={20} />
              </button>
            </>
          )}
        </div>

        {!loading && banners.length > 1 && (
          <div className="flex justify-center gap-2 mt-5">
            {banners.map((_, i) => (
              <button key={i} onClick={() => scrollTo(i)} className={`h-2.5 rounded-full transition-all duration-300 ${i === selectedIndex ? 'w-8 bg-[#7B2FBE]' : 'w-2.5 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};


// ===================== PRODI SECTION =====================
const prodis = [
  { id: 'kesmas', name: 'S1 Kesehatan Masyarakat', level: 'Sarjana (S1)', desc: 'Mencetak ahli kesehatan masyarakat yang profesional', icon: <Heart size={28} />, color: '#16A34A', bg: '#DCFCE7' },
  { id: 'keperawatan', name: 'S1 Ilmu Keperawatan', level: 'Sarjana (S1)', desc: 'Menghasilkan perawat kompeten dan berintegritas', icon: <Activity size={28} />, color: '#2563EB', bg: '#DBEAFE' },
  { id: 'farmasi', name: 'S1 Farmasi', level: 'Sarjana (S1)', desc: 'Mempersiapkan apoteker dan peneliti farmasi terbaik', icon: <FlaskConical size={28} />, color: '#7B2FBE', bg: '#F3F0FF' },
  { id: 'ners', name: 'Profesi Ners', level: 'Profesi', desc: 'Program profesi keperawatan standar nasional', icon: <Stethoscope size={28} />, color: '#0D9488', bg: '#CCFBF1' },
  { id: 'apoteker', name: 'Profesi Apoteker', level: 'Profesi', desc: 'Program profesi apoteker terakreditasi', icon: <Pill size={28} />, color: '#6B7280', bg: '#F3F4F6', comingSoon: true },
];

const PRODI_THEMES = {
  orange: { color: '#EA580C', bg: '#FFEDD5' },
  emerald: { color: '#059669', bg: '#D1FAE5' },
  blue: { color: '#2563EB', bg: '#DBEAFE' },
  red: { color: '#E11D48', bg: '#FFE4E6' },
  purple: { color: '#7B2FBE', bg: '#F3E8FF' },
};
const PRODI_ICONS = { Activity, HeartPulse: Heart, FlaskConical, Shield, GraduationCap, BookOpen };

const normalizeHomeProgram = (program) => {
  const Icon = PRODI_ICONS[program.icon] || BookOpen;
  const theme = PRODI_THEMES[program.theme] || PRODI_THEMES.purple;
  return {
    id: program.slug,
    name: program.name,
    level: program.level,
    desc: program.description,
    icon: <Icon size={28} />,
    comingSoon: program.comingSoon,
    ...theme,
  };
};

const ProdiCard = ({ prodi, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    whileHover={{ y: -10, transition: { duration: 0.3 } }}
    data-testid={`prodi-card-${index}`}
    className="h-full"
  >
    <Link
      to={prodi.comingSoon ? '#' : `/program-studi/${prodi.id}`}
      className="relative block h-full rounded-[2rem] overflow-hidden border-2 border-transparent
        bg-white dark:bg-[#1A182E] p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] 
        hover:shadow-[0_20px_50px_rgba(0,0,0,0.1)] transition-all duration-500 group"
      style={{
        '--accent-color': prodi.color,
      }}
    >
      {/* Corner Accent Border - Hover Only */}
      <div className="absolute inset-0 border-2 border-transparent group-hover:border-[var(--accent-color)] opacity-0 group-hover:opacity-20 transition-all duration-500 rounded-[2rem] pointer-events-none" />

      {/* Segera Hadir Badge - Adjusted Position */}
      {prodi.comingSoon && (
        <div className="absolute top-0 right-0 z-30 overflow-hidden w-28 h-28 pointer-events-none">
          <div className="absolute top-[20px] right-[-35px] rotate-45 bg-[#1A1A2E] dark:bg-purple-600 text-white text-[8px] font-bold py-1.5 w-32 text-center uppercase tracking-widest shadow-lg">
            SOON
          </div>
        </div>
      )}

      {/* Dynamic Background Glow */}
      <div className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full opacity-0 group-hover:opacity-20 blur-3xl transition-opacity duration-500 pointer-events-none"
        style={{ backgroundColor: prodi.color }} />

      <div className="relative z-20 h-full flex flex-col">
        <div className="flex justify-between items-start mb-5 sm:mb-6">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 shadow-md"
            style={{ backgroundColor: prodi.bg, color: prodi.color }}>
            {prodi.icon}
          </div>
          <div className="px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 border border-gray-100 dark:border-gray-800 mr-4">
            {prodi.level}
          </div>
        </div>

        <h3 className="text-lg sm:text-xl font-bold text-[#1A1A2E] dark:text-white mb-2 font-heading leading-tight group-hover:text-[var(--accent-color)] transition-colors">{prodi.name}</h3>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-6 sm:mb-8 leading-relaxed line-clamp-2">
          {prodi.desc}
        </p>

        <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-50 dark:border-gray-800/50">
          <div className="flex items-center gap-2">
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all" style={{ color: prodi.color }}>
              Detail Prodi
            </span>
            <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" style={{ color: prodi.color }} />
          </div>
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-50 dark:bg-gray-800/50 flex items-center justify-center group-hover:bg-[var(--accent-color)] group-hover:text-white transition-all duration-500 shadow-sm">
            <ChevronRight size={16} />
          </div>
        </div>
      </div>
    </Link>
  </motion.div>
);

const ProdiSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [programs, setPrograms] = useState(prodis);

  useEffect(() => {
    let active = true;
    fetchStudyPrograms()
      .then((records) => {
        if (active && records.length) setPrograms(records.map(normalizeHomeProgram));
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const interval = setInterval(() => {
      if (emblaApi) emblaApi.scrollNext();
    }, 4000);
    return () => clearInterval(interval);
  }, [emblaApi]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  return (
    <section data-testid="prodi-section" className="py-24 bg-white dark:bg-[#121020] relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-50/50 dark:bg-purple-900/5 rounded-full blur-3xl -mr-64 -mt-64 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-purple-50 dark:bg-purple-900/20 text-[#7B2FBE] dark:text-purple-400 font-bold text-xs uppercase tracking-widest mb-4">
            AKADEMIK & PROGRAM
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#1A1A2E] dark:text-white mb-6 font-heading">
            Program Studi Unggulan
          </h2>
          <div className="w-20 h-1.5 bg-gradient-to-r from-[#7B2FBE] to-[#9333EA] mx-auto rounded-full mb-6" />
          <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto text-lg leading-relaxed">
            Pilih jalur karir kesehatan impian Anda bersama lima program studi terakreditasi kami yang dirancang sesuai kebutuhan industri kesehatan global.
          </p>
        </motion.div>

        <div className="relative group">
          <div className="overflow-hidden -mx-4 px-4 py-6" ref={emblaRef}>
            <div className="flex -ml-4">
              {programs.map((prodi, i) => (
                <div key={i} className="flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_33.333333%] min-w-0 pl-4">
                  <ProdiCard prodi={prodi} index={i} />
                </div>
              ))}
            </div>
          </div>

          <button onClick={scrollPrev} className="absolute left-0 lg:-left-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white dark:bg-[#201E38] text-gray-400 hover:text-[#7B2FBE] shadow-xl border border-gray-100 dark:border-gray-800 flex items-center justify-center transition-all hover:scale-110 z-10 opacity-0 group-hover:opacity-100 disabled:opacity-0 hidden lg:flex">
            <ChevronLeft size={24} />
          </button>
          <button onClick={scrollNext} className="absolute right-0 lg:-right-5 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white dark:bg-[#201E38] text-gray-400 hover:text-[#7B2FBE] shadow-xl border border-gray-100 dark:border-gray-800 flex items-center justify-center transition-all hover:scale-110 z-10 opacity-0 group-hover:opacity-100 disabled:opacity-0 hidden lg:flex">
            <ChevronRight size={24} />
          </button>
        </div>

        <div className="text-center mt-12 md:mt-20">
          <Link to="/program-studi"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-[#7B2FBE] to-[#9333EA] text-white px-10 py-4 rounded-full font-bold text-sm shadow-xl shadow-purple-500/20 hover:shadow-purple-500/40 hover:scale-105 active:scale-95 transition-all duration-300">
            LIHAT SEMUA PROGRAM STUDI
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ===================== BERITA SECTION =====================
const BeritaCardSkeleton = () => (
  <div className="rounded-2xl overflow-hidden bg-white dark:bg-[#1A182E] border border-gray-100 dark:border-gray-800 animate-pulse">
    <div className="h-48 bg-gray-200 dark:bg-gray-800" />
    <div className="p-5 space-y-3">
      <div className="flex gap-2">
        <div className="h-5 w-20 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="h-5 w-24 rounded-full bg-gray-200 dark:bg-gray-700" />
      </div>
      <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-700" />
      <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="h-3 w-full rounded bg-gray-200 dark:bg-gray-700" />
      <div className="h-3 w-5/6 rounded bg-gray-200 dark:bg-gray-700" />
    </div>
  </div>
);

const BeritaSection = () => {
  const [beritaList, setBeritaList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBeritaList(3).then((data) => {
      setBeritaList(data.slice(0, 3));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <section data-testid="berita-section" className="py-20 bg-[#F8F7FF] dark:bg-[#100E1C]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <span className="text-[#7B2FBE] font-semibold text-sm uppercase tracking-wider">Informasi Terkini</span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] dark:text-white mt-2 mb-4 font-heading">Berita & Pengumuman Terbaru</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => <BeritaCardSkeleton key={i} />)
            : beritaList.map((article, i) => {
              const thumbUrl = getImageUrl(article.thumbnail, { width: 800, height: 450, quality: 80, format: 'webp' });
              return (
                <motion.div
                  key={article.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  data-testid={`news-card-${i}`}
                >
                  <Link
                    to={`/berita/${article.slug}`}
                    className="block group rounded-2xl overflow-hidden bg-white dark:bg-[#1A182E] border border-gray-100 dark:border-gray-800 card-hover h-full flex flex-col"
                  >
                    <div className="relative overflow-hidden h-48 flex-shrink-0">
                      {thumbUrl
                        ? <img src={thumbUrl} alt={article.judul} style={{ objectPosition: article.thumbnailPosition || '50% 50%' }} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                        : <div className="w-full h-full bg-gradient-to-br from-[#7B2FBE] to-[#1A1A2E]" />
                      }
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        {article.kategori && (
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                            {article.kategori.charAt(0).toUpperCase() + article.kategori.slice(1)}
                          </span>
                        )}
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <Calendar size={11} />
                          {formatTanggalIndonesia(article.tanggal_publikasi)}
                        </span>
                      </div>
                      <h3 className="font-bold text-[#1A1A2E] dark:text-white mb-2 leading-snug text-base font-heading line-clamp-2 group-hover:text-[#7B2FBE] transition-colors">
                        {article.judul}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-3 mb-4 flex-1">
                        {article.Excerpt}
                      </p>
                      <span className="text-sm font-semibold text-[#7B2FBE] dark:text-[#9333EA] flex items-center gap-1 group-hover:gap-2 transition-all mt-auto">
                        Baca Selengkapnya <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })
          }
        </div>

        <div className="text-center mt-10">
          <Link to="/berita" data-testid="lihat-semua-berita-btn"
            className="inline-flex items-center gap-2 border-2 border-[#7B2FBE] text-[#7B2FBE] dark:text-[#9333EA] dark:border-[#9333EA]
              px-7 py-3 rounded-full font-semibold text-sm hover:bg-[#7B2FBE] hover:text-white dark:hover:bg-[#9333EA] transition-all duration-300 hover:scale-105">
            Lihat Semua Berita <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ===================== GALERI SECTION =====================
const GALERI_FALLBACK = [
  { id: 'f1', img: CAMPUS.garden, alt: 'Taman kampus STIKES HI' },
  { id: 'f2', img: CAMPUS.lecture, alt: 'Kegiatan perkuliahan' },
  { id: 'f3', img: CAMPUS.lab, alt: 'Praktikum laboratorium' },
  { id: 'f4', img: CAMPUS.students, alt: 'Kegiatan mahasiswa' },
];

const GaleriLightbox = ({ images, index, onClose }) => {
  const [current, setCurrent] = useState(index);
  const item = images[current];
  const src = item.foto
    ? getImageUrl(item.foto, { width: 1200, quality: 90, format: 'webp' })
    : item.img;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setCurrent((c) => (c - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setCurrent((c) => (c + 1) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [images.length, onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Close */}
      <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white p-2 transition-colors z-10">
        <X size={28} />
      </button>
      {/* Counter */}
      <span className="absolute top-5 left-1/2 -translate-x-1/2 text-white/50 text-sm z-10">
        {current + 1} / {images.length}
      </span>
      {/* Image */}
      <motion.img
        key={current}
        src={src}
        alt={item.alt}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className="max-h-[85vh] max-w-full rounded-xl object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      {/* Caption */}
      {item.alt && (
        <p className="absolute bottom-6 text-white/60 text-sm text-center px-8">{item.alt}</p>
      )}
      {/* Prev / Next */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent((c) => (c - 1 + images.length) % images.length); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 transition-all"
          ><ChevronLeft size={22} /></button>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent((c) => (c + 1) % images.length); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/25 text-white rounded-full p-3 transition-all"
          ><ChevronRight size={22} /></button>
        </>
      )}
    </motion.div>
  );
};

const GaleriSection = () => {
  const [images, setImages] = useState([]);
  const [lightbox, setLightbox] = useState(null); // index

  useEffect(() => {
    fetchGaleriBeranda().then((data) => {
      if (data.length > 0) setImages(data);
      else setImages(GALERI_FALLBACK);
    });
  }, []);

  return (
    <section data-testid="galeri-section" className="py-20 bg-white dark:bg-[#121020]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <span className="text-[#7B2FBE] font-semibold text-sm uppercase tracking-wider">Momen Berharga</span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] dark:text-white mt-2 font-heading">Galeri Kegiatan Kampus</h2>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((item, i) => {
            const src = item.foto
              ? getImageUrl(item.foto, { width: 600, height: 600, quality: 80, format: 'webp' })
              : item.img;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                data-testid={`galeri-img-${i}`}
                className="relative overflow-hidden rounded-xl group cursor-pointer aspect-square"
                onClick={() => setLightbox(i)}
              >
                <img src={src} alt={item.alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center gap-2 flex-col">
                  <ZoomIn size={26} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs font-medium px-3 text-center line-clamp-2">{item.alt}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Link to="/galeri" data-testid="lihat-galeri-btn"
            className="inline-flex items-center gap-2 bg-[#7B2FBE] text-white px-7 py-3 rounded-full font-semibold text-sm hover:bg-[#9333EA] transition-all duration-300 hover:scale-105 shadow-lg shadow-purple-500/25">
            Lihat Galeri Lengkap <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <GaleriLightbox images={images} index={lightbox} onClose={() => setLightbox(null)} />
        )}
      </AnimatePresence>
    </section>
  );
};

// ===================== AGENDA SECTION =====================
const formatAgendaWaktu = (mulai, selesai) => {
  if (!mulai) return '-';

  const getT = (val) => new Date(val).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }).replace('.', ':');

  const d1 = formatTanggalIndonesia(mulai);
  const t1 = getT(mulai);

  if (!selesai) return `Mulai: ${d1}, ${t1} WIB`;

  const d2 = formatTanggalIndonesia(selesai);
  const t2 = getT(selesai);

  if (d1 === d2) {
    return `${d1}, ${t1} - ${t2} WIB`;
  }
  return `${d1} ${t1} WIB s/d ${d2} ${t2} WIB`;
};

const AgendaCardSkeleton = () => (
  <div className="flex gap-5 p-6 rounded-2xl bg-white dark:bg-[#1A182E] border-l-4 border-gray-200 dark:border-gray-800 animate-pulse">
    <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-gray-200 dark:bg-gray-800" />
    <div className="flex-1 space-y-3 py-1">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
    </div>
  </div>
);

const AgendaCard = ({ agenda, index, BULAN_SINGKAT }) => {
  let day = '-';
  let month = '-';
  if (agenda.tanggal_mulai) {
    const dateObj = new Date(agenda.tanggal_mulai);
    day = dateObj.getDate().toString();
    month = BULAN_SINGKAT[dateObj.getMonth()];
  }
  const timeStr = formatAgendaWaktu(agenda.tanggal_mulai, agenda.tanggal_selesai);
  
  const statusBadge = agenda.isOngoing 
    ? <span className="absolute top-2 right-2 bg-blue-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1">
        <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
        <span className="w-1.5 h-1.5 bg-white rounded-full absolute" />
        LIVE
      </span>
    : agenda._computedStatus === 'past'
    ? <span className="absolute top-2 right-2 bg-gray-500 text-white px-2 py-0.5 rounded-full text-[10px] font-bold">
        SELESAI
      </span>
    : null;

  return (
    <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="relative flex gap-5 p-6 rounded-2xl bg-white dark:bg-[#1A182E] border-l-4 border-[#7B2FBE] shadow-sm hover:shadow-md transition-shadow">
      {statusBadge}
      <div className="flex-shrink-0 w-16 h-16 rounded-xl flex flex-col items-center justify-center bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] text-white">
        <span className="text-2xl font-bold leading-tight font-heading">{day}</span>
        <span className="text-xs uppercase tracking-wide">{month}</span>
      </div>
      <div className="flex-1">
        <h3 className="font-bold text-[#1A1A2E] dark:text-white text-sm leading-snug mb-2 font-heading">{agenda.judul}</h3>
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1">
          <MapPin size={12} className="text-[#7B2FBE]" /> {agenda.lokasi || '-'}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <Clock size={12} className="text-[#7B2FBE]" /> {timeStr}
        </div>
      </div>
    </motion.div>
  );
};

const AgendaSection = () => {
  const [agendaList, setAgendaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const BULAN_SINGKAT = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

  useEffect(() => {
    fetchAgenda(-1).then((data) => {
      const currentDate = new Date();
      
      const processedData = data.map(ev => {
        const startDate = ev.tanggal_mulai ? new Date(ev.tanggal_mulai) : null;
        const endDate = ev.tanggal_selesai ? new Date(ev.tanggal_selesai) : startDate;
        const isPast = endDate && endDate < currentDate;
        const isOngoing = startDate && startDate <= currentDate && !isPast;
        
        return {
          ...ev,
          isOngoing,
          _computedStatus: isPast ? 'past' : 'upcoming'
        };
      });

      const ongoing = processedData.filter(e => e.isOngoing).sort((a, b) => new Date(a.tanggal_mulai) - new Date(b.tanggal_mulai));
      const upcoming = processedData.filter(e => e._computedStatus === 'upcoming' && !e.isOngoing).sort((a, b) => new Date(a.tanggal_mulai) - new Date(b.tanggal_mulai));
      const past = processedData.filter(e => e._computedStatus === 'past').sort((a, b) => new Date(b.tanggal_mulai) - new Date(a.tanggal_mulai));

      let finalList = [...ongoing, ...upcoming];
      if (finalList.length === 0) {
        finalList = past.slice(0, 6);
      }

      setAgendaList(finalList);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!emblaApi || agendaList.length <= 3) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    const interval = setInterval(() => { if (emblaApi) emblaApi.scrollNext(); }, 5000);
    return () => { clearInterval(interval); emblaApi.off('select', onSelect); };
  }, [emblaApi, agendaList.length]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();

  const useCarousel = agendaList.length > 3;

  return (
    <section data-testid="agenda-section" className="py-20 bg-[#F8F7FF] dark:bg-[#100E1C]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
          <span className="text-[#7B2FBE] font-semibold text-sm uppercase tracking-wider">Jadwal Kegiatan</span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] dark:text-white mt-2 font-heading">
            {agendaList.some(a => a.isOngoing) ? 'Agenda Berlangsung & Mendatang' : agendaList.length > 0 ? (agendaList[0]._computedStatus === 'past' ? 'Agenda Terbaru' : 'Agenda Mendatang') : 'Agenda Mendatang'}
          </h2>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => <AgendaCardSkeleton key={i} />)}
          </div>
        ) : useCarousel ? (
          <div className="relative">
            <div className="overflow-hidden p-4 -m-4" ref={emblaRef}>
              <div className="flex z-10 w-full" style={{ touchAction: 'pan-y' }}>
                {agendaList.map((agenda, i) => (
                  <div key={agenda.id} className="flex-[0_0_100%] md:flex-[0_0_33.333%] min-w-0 pr-6">
                    <AgendaCard agenda={agenda} index={i} BULAN_SINGKAT={BULAN_SINGKAT} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center gap-3 mt-8">
              <button onClick={scrollPrev} className="w-10 h-10 rounded-full bg-white dark:bg-[#1A182E] text-gray-400 hover:text-[#7B2FBE] shadow-md border border-gray-100 dark:border-gray-800 flex items-center justify-center transition-colors">
                <ChevronLeft size={20} />
              </button>
              <div className="flex items-center gap-2">
                {agendaList.map((_, i) => (
                  <button key={i} onClick={() => emblaApi?.scrollTo(i)}
                    className={`w-2 h-2 rounded-full transition-all ${selectedIndex === i ? 'bg-[#7B2FBE] w-6' : 'bg-gray-300 dark:bg-gray-600'}`} />
                ))}
              </div>
              <button onClick={scrollNext} className="w-10 h-10 rounded-full bg-white dark:bg-[#1A182E] text-gray-400 hover:text-[#7B2FBE] shadow-md border border-gray-100 dark:border-gray-800 flex items-center justify-center transition-colors">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {agendaList.map((agenda, i) => (
              <AgendaCard key={agenda.id} agenda={agenda} index={i} BULAN_SINGKAT={BULAN_SINGKAT} />
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <Link to="/agenda" data-testid="lihat-semua-agenda-btn"
            className="inline-flex items-center gap-2 border-2 border-[#7B2FBE] text-[#7B2FBE] dark:text-[#9333EA] dark:border-[#9333EA]
              px-7 py-3 rounded-full font-semibold text-sm hover:bg-[#7B2FBE] hover:text-white dark:hover:bg-[#9333EA] transition-all duration-300 hover:scale-105">
            Lihat Semua Agenda <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ===================== TESTIMONI SECTION =====================
const fallbackTestimonials = [
  { quote: 'STIKES HI Jambi memberikan pendidikan kesehatan terbaik. Dosen-dosennya sangat berpengalaman dan fasilitas laboratoriumnya lengkap. Saya berhasil meraih karir impian sebagai perawat profesional.', name: 'Rini Anggraini, S.Kep', prodi: 'S1 Ilmu Keperawatan', tahun: 'Alumni 2020', initials: 'RA', color: '#2563EB' },
  { quote: 'Pengalaman kuliah di STIKES HI sangat luar biasa. Program Farmasi di sini memiliki kurikulum yang up-to-date dan praktik langsung yang mempersiapkan kami untuk dunia kerja profesional.', name: 'Ahmad Fauzi, S.Farm., Apt.', prodi: 'S1 Farmasi', tahun: 'Alumni 2021', initials: 'AF', color: '#7B2FBE' },
  { quote: 'Berkuliah di STIKES HI adalah keputusan terbaik saya. Program Kesehatan Masyarakat mengajarkan saya untuk berkontribusi nyata bagi kesehatan masyarakat Indonesia yang lebih baik.', name: 'Sari Dewi Putri, SKM', prodi: 'S1 Kesehatan Masyarakat', tahun: 'Alumni 2019', initials: 'SD', color: '#16A34A' },
];

// Konstanta, bukan turunan state: ditaruh di lingkup modul supaya identitasnya
// stabil dan tidak perlu masuk daftar dependensi useEffect.
const prodiMap = {
  's1_kesehatan_masyarakat': { label: 'S1 Kesehatan Masyarakat', color: '#16A34A' },
  's1_ilmu_keperawatan': { label: 'S1 Ilmu Keperawatan', color: '#2563EB' },
  's1_keperawatan': { label: 'S1 Ilmu Keperawatan', color: '#2563EB' },
  's1_farmasi': { label: 'S1 Farmasi', color: '#7B2FBE' },
  'profesi_ners': { label: 'Profesi Ners', color: '#0D9488' },
  'profesi_apoteker': { label: 'Profesi Apoteker', color: '#6B7280' },
};

const TestimoniSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch dari CMS
  useEffect(() => {
    fetchTestimoni().then((data) => {
      const mappedData = data.map((item) => {
        const prodi = prodiMap[item.prodi] || { label: item.prodi || '', color: '#7B2FBE' };
        return {
          quote: item.testimoni,
          name: item.nama,
          prodi: prodi.label,
          jabatan: item.jabatan || '',
          tahun: item.tahun_lulus ? `Alumni ${item.tahun_lulus}` : '',
          initials: item.nama ? item.nama.substring(0, 2).toUpperCase() : '??',
          color: prodi.color,
          foto: item.foto || null,
        };
      });
      const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5).slice(0, 6);
      setTestimonials(mappedData.length > 0 ? shuffle(mappedData) : fallbackTestimonials);
      setLoading(false);
    }).catch(() => {
      setTestimonials(fallbackTestimonials);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    const interval = setInterval(() => { if (emblaApi) emblaApi.scrollNext(); }, 6000);
    return () => { clearInterval(interval); emblaApi.off('select', onSelect); };
  }, [emblaApi]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();
  const scrollTo = (index) => emblaApi && emblaApi.scrollTo(index);

  return (
    <section data-testid="testimoni-section" className="py-24 bg-[#F5F2FA] dark:bg-[#110F1E] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-8 items-center">
          {/* Bagian Kiri: Teks */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:pr-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[#7B2FBE] text-xl font-bold">{'>'}</span>
              <span className="bg-white dark:bg-white/10 text-gray-800 dark:text-gray-200 px-4 py-1.5 rounded-full text-sm font-semibold shadow-sm border border-gray-100 dark:border-gray-800">Testimoni Alumni</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#1A1A2E] dark:text-white mb-4 font-heading leading-snug">
              26 Tahun Lebih Berpengalaman dalam Mencetak Tenaga Ahli Kesehatan
            </h2>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
              STIKES HI Jambi (Berdiri Tahun 2000) telah meluluskan ribuan alumni profesional untuk kemajuan kesehatan di Indonesia. Ribuan alumni telah tersebar ke pelosok negeri agar pelayanan kesehatan dapat secara merata terjangkau di berbagai fasilitas kesehatan pemerintah maupun swasta.
            </p>
            <div className="hidden lg:flex gap-4">
              <button onClick={scrollPrev} className="w-12 h-12 rounded-full bg-white dark:bg-[#201E38] text-gray-400 hover:text-[#7B2FBE] shadow-md border border-gray-100 dark:border-gray-800 flex items-center justify-center transition-colors">
                <ChevronLeft size={24} />
              </button>
              <button onClick={scrollNext} className="w-12 h-12 rounded-full bg-white dark:bg-[#201E38] text-gray-400 hover:text-[#7B2FBE] shadow-md border border-gray-100 dark:border-gray-800 flex items-center justify-center transition-colors">
                <ChevronRight size={24} />
              </button>
            </div>
          </motion.div>

          {/* Bagian Kanan: Carousel Testimoni */}
          <div className="lg:col-span-2 relative">
            <div className="overflow-hidden p-4 -m-4" ref={emblaRef}>
              <div className="flex z-10 w-full" style={{ touchAction: 'pan-y' }}>
                {testimonials.map((t, i) => (
                  <div key={i} className="flex-[0_0_90%] md:flex-[0_0_50%] min-w-0 pr-6">
                    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                      className="bg-white dark:bg-[#1A182E] rounded-3xl p-8 shadow-sm hover:shadow-md border border-gray-100 dark:border-[#1F1F2E] transition-shadow h-full flex flex-col justify-between"
                    >
                      <div>
                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8 text-sm md:text-base">
                          "{t.quote}"
                        </p>
                      </div>
                      <div className="flex items-center gap-4 mt-auto">
                        {t.foto
                          ? <img src={getImageUrl(t.foto, { width: 56, height: 56, quality: 80, format: 'webp', fit: 'cover' })} alt={t.name} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
                          : <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0" style={{ backgroundColor: t.color }}>{t.initials}</div>
                        }
                        <div>
                          <div className="font-bold text-[#1A1A2E] dark:text-white text-sm font-heading">{t.name}</div>
                          {t.jabatan && <div className="text-xs text-gray-500 dark:text-gray-400 leading-tight mt-0.5">{t.jabatan}</div>}
                          <div className="text-xs text-[#7B2FBE] font-medium leading-tight mt-1">{t.prodi}</div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">{t.tahun}</div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigasi Mobile Bawah */}
            <div className="flex justify-center items-center gap-8 mt-8 lg:hidden">
              <button onClick={scrollPrev} className="text-gray-400 hover:text-[#7B2FBE]"><ChevronLeft size={24} /></button>
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button key={i} onClick={() => scrollTo(i)} className={`h-2.5 rounded-full transition-all duration-300 ${i === selectedIndex ? 'w-8 bg-[#7B2FBE]' : 'w-2.5 bg-gray-300 dark:bg-gray-700'}`} />
                ))}
              </div>
              <button onClick={scrollNext} className="text-gray-400 hover:text-[#7B2FBE]"><ChevronRight size={24} /></button>
            </div>

            {/* Dots untuk Desktop */}
            <div className="hidden lg:flex justify-end gap-2 mt-8 pr-6">
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => scrollTo(i)} className={`h-2 rounded-full transition-all duration-300 ${i === selectedIndex ? 'w-8 bg-[#7B2FBE]' : 'w-2 bg-gray-300 dark:bg-gray-700'}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ===================== CTA SECTION =====================
const CTASection = () => (
  <section data-testid="cta-section" className="relative py-20 overflow-hidden"
    style={{ background: 'linear-gradient(135deg, #4C1D95 0%, #7B2FBE 50%, #6D28D9 100%)' }}>
    <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-white/5 blur-3xl pointer-events-none" />
    <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-white/5 blur-3xl pointer-events-none" />
    <div className="max-w-3xl mx-auto px-4 text-center relative z-10">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
        <span className="inline-block bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-4 py-1.5 text-white text-xs font-semibold mb-6 uppercase tracking-wide">
          PMB 2026/2027
        </span>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 font-heading leading-tight">
          Wujudkan Impianmu Bersama STIKES HI Jambi
        </h2>
        <p className="text-white/80 text-lg mb-8 leading-relaxed">
          Pendaftaran Mahasiswa Baru 2026/2027 kini telah dibuka. Bergabunglah dengan ribuan mahasiswa yang telah mewujudkan karir kesehatan impian mereka.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="https://pmb.stikes-hi.ac.id/" target="_blank" rel="noopener noreferrer"
            data-testid="cta-daftar-btn"
            className="bg-white text-[#7B2FBE] px-8 py-3.5 rounded-full font-bold text-sm hover:bg-purple-50 transition-all duration-300 hover:scale-105 active:scale-95 shadow-lg">
            Daftar Sekarang
          </a>
          <Link to="/program-studi"
            className="border-2 border-white/60 text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-white/10 transition-all duration-300 hover:scale-105">
            Lihat Program Studi
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

// ===================== MAIN HOME =====================
const Home = () => (
  <div data-testid="home-page">
    <SEO
      title="Kampus Kesehatan Unggulan Jambi"
      description="STIKES Harapan Ibu Jambi - Sekolah Tinggi Ilmu Kesehatan Unggulan, Berkarakter, dan Berdaya Saing di Jambi. Program Keperawatan, Farmasi, Kesehatan Masyarakat, dan Profesi Ners."
    />
    <HomePopupModal />
    <HeroSection />
    <BannerSection />
    <ProdiSection />
    <LazySection><BeritaSection /></LazySection>
    <LazySection><GaleriSection /></LazySection>
    <LazySection><AgendaSection /></LazySection>
    <LazySection><TestimoniSection /></LazySection>
    <LazySection><CTASection /></LazySection>
  </div>
);

export default Home;
